import React, { useState } from 'react';
import { ActivityIndicator, Linking, StyleSheet, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { toastActions } from '../../store/reducers/toast';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Text from '../global/Text';
import fontMaker from '../../utils/font';
import moment from 'moment';
import { ReportSummaryLite } from '../../store/reducers/siteReport/type';

type ReportsListItemProps = {
  item: ReportSummaryLite;
};

const ReportsListItem = ({ item }: ReportsListItemProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('home');
  const [downloading, setDownloading] = useState(false);
  const hasDownloadUrl = !!item.downloadUrl?.trim();

  const handleDownload = async () => {
    if (!hasDownloadUrl) {
      dispatch(toastActions.createToast(t('reports.downloadPreparing'), true));
      return;
    }
    try {
      setDownloading(true);
      await Linking.openURL(item.downloadUrl);
    } catch {
      dispatch(toastActions.createToast(t('reports.downloadFailed'), true));
    } finally {
      setDownloading(false);
    }
  };

  const scheduledDateFormatted = item.scheduledDate ? moment(item.scheduledDate).format('YYYY-MM-DD') : '';

  const submittedAtFormatted = item.submittedAt ? moment(item.submittedAt).format('DD MMM YYYY, hh:mm A') : '';

  const typeLabel = item.isAdhoc ? t('reports.adhoc') : t('reports.scheduled');

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.metaRow}>
          {!!scheduledDateFormatted && <Text style={styles.scheduledDate}>{scheduledDateFormatted}</Text>}
          {!!scheduledDateFormatted && <View style={styles.dot} />}
          <Text style={styles.typeLabel}>{typeLabel}</Text>
        </View>
        <TouchableOpacity
          style={styles.downloadBtn}
          onPress={handleDownload}
          disabled={downloading}
          testID="report-download-btn"
        >
          {downloading ? (
            <ActivityIndicator size="small" color="#3CD070" />
          ) : (
            <Icon
              name={hasDownloadUrl ? 'arrow-down' : 'progress-clock'}
              size={20}
              color={hasDownloadUrl ? '#3CD070' : '#6B7280'}
            />
          )}
        </TouchableOpacity>
      </View>

      <Text style={styles.title} numberOfLines={2}>
        {item.questionnaireName}
      </Text>

      <View style={styles.flagRow}>
        <View style={styles.flagItem}>
          <View style={[styles.flagBadge, styles.flagGreen]}>
            <Icon name="flag" size={12} color="#fff" />
          </View>
          <Text style={styles.flagCount}>{item.flagCount?.green ?? 0}</Text>
        </View>
        <View style={styles.flagSep} />
        <View style={styles.flagItem}>
          <View style={[styles.flagBadge, styles.flagYellow]}>
            <Icon name="flag" size={12} color="#fff" />
          </View>
          <Text style={styles.flagCount}>{item.flagCount?.yellow ?? 0}</Text>
        </View>
        <View style={styles.flagSep} />
        <View style={styles.flagItem}>
          <View style={[styles.flagBadge, styles.flagRed]}>
            <Icon name="flag" size={12} color="#fff" />
          </View>
          <Text style={styles.flagCount}>{item.flagCount?.red ?? 0}</Text>
        </View>
      </View>

      <View style={styles.footer}>
        <View style={styles.infoRow}>
          <Icon name="map-marker-outline" size={14} color="#6B7280" />
          <Text style={styles.footerText} numberOfLines={1}>
            {item.siteName}
          </Text>
        </View>
        {!!submittedAtFormatted && (
          <View style={[styles.infoRow, styles.infoRowSecondary]}>
            <Icon name="clipboard-check-outline" size={14} color="#6B7280" />
            <Text style={styles.footerText} numberOfLines={1}>
              {submittedAtFormatted}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
};

export default ReportsListItem;

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E7EDF3',
    shadowColor: '#0F172A',
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12
  },
  scheduledDate: {
    fontSize: 12,
    color: '#6B7280',
    ...fontMaker({ weight: 'Medium' })
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#9CA3AF',
    marginHorizontal: 6
  },
  typeLabel: {
    fontSize: 12,
    color: '#6B7280',
    ...fontMaker({ weight: 'Regular' })
  },
  downloadBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E5EAF0',
    alignItems: 'center',
    justifyContent: 'center'
  },
  title: {
    fontSize: 14,
    lineHeight: 20,
    color: '#111827',
    ...fontMaker({ weight: 'SemiBold' }),
    marginBottom: 8
  },
  flagRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10
  },
  flagItem: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  flagBadge: {
    width: 20,
    height: 20,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center'
  },
  flagGreen: { backgroundColor: '#0ABA26' },
  flagYellow: { backgroundColor: '#FFC81F' },
  flagRed: { backgroundColor: '#F7001A' },
  flagCount: {
    fontSize: 13,
    color: '#374151',
    marginLeft: 4,
    ...fontMaker({ weight: 'SemiBold' })
  },
  flagSep: {
    width: 1,
    height: 14,
    backgroundColor: '#E5E7EB',
    marginHorizontal: 10
  },
  footer: {
    marginTop: 2
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1
  },
  infoRowSecondary: {
    marginTop: 8
  },
  footerText: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 4,
    flex: 1
  }
});
